import { useEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import type { ApiClient, Fachbereich, Haus, ProjektDetail, ProfilEintrag } from '../api';
import { Fehler } from './Projekte';
import { gueltigeWahl, hausText, istMehrhaus } from '../mehrhaus';

/**
 * Was die Reiter vom Rahmen bekommen.
 *
 * Das Projekt und der Fachbereichskatalog werden einmal geladen und
 * weitergegeben, statt in jedem Reiter erneut. Bei zehn Reitern waeren das
 * sonst zehn Abfragen fuer dieselben Daten.
 */
export interface Rahmen {
  api: ApiClient;
  projekt: ProjektDetail;
  bereiche: Fachbereich[];
  profile: ProfilEintrag[];
  neuLaden: () => void;
  /**
   * F60 H2b: die Häuser des Projekts (leer, solange keine geladen sind) und die Haus-Wahl des Rahmens
   * („Alle“ = null). Die Wahl gibt es erst ab zwei aktiven Häusern (F60-2); sonst ist `haus` immer null und keine
   * Seite fragt mit `?haus=` an. Optional, damit Seiten und Tests ohne Rahmen unverändert bleiben.
   */
  haeuser?: Haus[];
  haus?: number | null;
  setHaus?: (haus: number | null) => void;
  haeuserNeuLaden?: () => void;
}

/**
 * Rahmen der Projektansicht: laedt Projekt, Fachbereichskatalog und Profile
 * einmal und reicht sie ueber den Outlet-Kontext an die Unterseiten weiter.
 *
 * ═══ DIE ZWEI WAAGERECHTEN MENUEZEILEN SIND ENTFALLEN ════════════════════
 *
 * Bis zum 17.09.2026 standen hier zwei Reiterzeilen: oben sechs feste
 * Punkte samt Schalter "Gewerkeplanungen", darunter aufgeklappt die
 * Gewerke aus dem Fachbereichskatalog. Zusammen mit der Navigationszeile in
 * `App.tsx` waren das drei waagerechte Menuezeilen uebereinander - genau
 * das, was der Auftrag vom 17.09.2026 beendet:
 *
 *   "Navigation darf niemals mehr wie bisher durch mehrere immer laenger
 *    werdende horizontale Menueleisten wachsen." (Abschnitt 42)
 *
 * Alle ihre Ziele stehen jetzt in der linken Seitenleiste des `AppShell`
 * (`navigation/struktur.ts` + der Fachbereichskatalog). KEIN Ziel ist dabei
 * verlorengegangen; `pruefung/routen-abgleich.mjs` prueft das bei jedem
 * Bau nach. Mitgewandert sind auch die Feinheiten der alten Zeile: die
 * Marke "vorb." fuer freigeschaltete, aber noch nicht aufgenommene
 * Bereiche steht jetzt am Seitenleisteneintrag.
 *
 * NICHT mitgewandert ist die rote Schrift der Koordination (Wunsch vom
 * 16.08.2026). Der neue Auftrag hebt sie ausdruecklich auf - Abschnitt 5
 * ("Rot NICHT als normale Navigationsfarbe") und Abschnitt 18
 * ("Koordination darf nicht pauschal rot erscheinen. Rot nur bei
 * tatsaechlichem Problem.").
 *
 * Was bleibt: das Laden und das Weiterreichen des Kontexts. Der Rahmen ist
 * damit eine reine Datenroute ohne eigene Darstellung.
 */
export function Projektrahmen({ api, bereicheVorab }: {
  api: ApiClient;
  /**
   * Der bereits in `App.tsx` geladene Fachbereichskatalog. Die Seitenleiste
   * braucht ihn auf jeder Seite, also wird er dort geladen; hier wird er
   * uebernommen statt ein zweites Mal abgefragt.
   */
  bereicheVorab?: Fachbereich[];
}) {
  const { id } = useParams<{ id: string }>();

  const [projekt, setProjekt] = useState<ProjektDetail | null>(null);
  const [bereiche, setBereiche] = useState<Fachbereich[] | null>(null);
  const [profile, setProfile] = useState<ProfilEintrag[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = () => {
    if (!id) return;
    api.projekt(id)
      .then(setProjekt)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => { laden(); }, [api, id]);

  // Die Fachbereiche DIESES Projekts — nicht der gemeinsame Katalog.
  //
  // Der Unterschied ist der Grund, warum ein Projekt eine eigene Gewerkeseite
  // oder einen weiteren Import bekommen kann, ohne dass ein zweiter Quellzweig
  // entsteht: Der Code liegt gemeinsam, die Erreichbarkeit ist Konfiguration.
  useEffect(() => {
    if (!id) return;

    // `App.tsx` hat den Katalog fuer die Seitenleiste ohnehin schon geladen.
    // Ein zweiter Abruf waere dieselbe Antwort ein zweites Mal - siehe den
    // Kommentar an `bereicheVorab`.
    if (bereicheVorab && bereicheVorab.length > 0) {
      setBereiche(bereicheVorab);
      return;
    }

    api.projektFachbereiche(id)
      .then(setBereiche)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, id, bereicheVorab]);

  // Die Profile haengen an der Revit-Fassung. Bei gemischten Faellen nehmen wir
  // die des ersten Modells - in der Praxis arbeitet ein Projekt mit einer.
  useEffect(() => {
    if (!id || !projekt || projekt.modelle.length === 0) return;
    api.profile(id, projekt.modelle[0].revitVersion)
      .then(setProfile)
      .catch(() => setProfile([]));
  }, [api, id, projekt]);

  // F60 H2b: Die Häuser laden. Ein Fehler (ältere API, Test ohne Attrappe) heißt „keine Häuser“ - dann bleibt alles
  // wie bei einem Haus. Über Promise.resolve, damit auch ein synchron werfender Aufruf nur ein Fehlschlag ist. Eine
  // leere Antwort setzt keinen Zustand: Bei einem Haus soll der Rahmen nicht einmal neu zeichnen.
  const [haeuser, setHaeuser] = useState<Haus[]>([]);
  const [hausWahl, setHausWahl] = useState<number | null>(null);

  const haeuserLaden = () => {
    if (!id) return;
    Promise.resolve()
      .then(() => api.haeuser(id))
      .then((h) => { if (Array.isArray(h) && h.length > 0) setHaeuser(h); })
      .catch(() => { /* ohne Häuser wie bei einem Haus */ });
  };

  // Beim Projektwechsel die Häuser des alten Projekts verwerfen - funktional, damit ein leerer Stand nicht neu zeichnet.
  useEffect(() => {
    setHausWahl(null);
    setHaeuser((alt) => (alt.length > 0 ? [] : alt));
    haeuserLaden();
  }, [api, id]);

  const haus = gueltigeWahl(hausWahl, haeuser);

  const rahmen = useMemo<Rahmen | null>(
    () => (projekt && bereiche
      ? {
        api, projekt, bereiche, profile, neuLaden: laden,
        haeuser, haus, setHaus: setHausWahl, haeuserNeuLaden: haeuserLaden,
      }
      : null),
    [api, projekt, bereiche, profile, haeuser, haus],
  );

  if (fehler) return <Fehler text={fehler} />;
  if (!rahmen || !id) return <div className="leer">wird geladen …</div>;

  // Bei einem Haus genau wie bisher: nur der Outlet, keine eigene Darstellung.
  if (!istMehrhaus(haeuser)) return <Outlet context={rahmen} />;

  return (
    <>
      <HausWahl haeuser={haeuser} haus={haus} setHaus={setHausWahl} />
      <Outlet context={rahmen} />
    </>
  );
}

/**
 * F60 H2b: Haus-Wahl „Alle / Haus 01 / …“ im Projektrahmen, nur im Mehrhausprojekt. Raumbuch und Türbuch lesen sie
 * aus dem Rahmen; die übrigen Seiten folgen mit ihren Gewerken (H3).
 */
export function HausWahl({ haeuser, haus, setHaus }: {
  haeuser: Haus[];
  haus: number | null;
  setHaus: (haus: number | null) => void;
}) {
  const aktive = haeuser
    .filter((h) => h.isActive)
    .sort((a, b) => a.reihenfolge - b.reihenfolge || a.nummer - b.nummer);

  return (
    <div className="hauswahl" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px' }}>
      <label htmlFor="hauswahl" className="schwach">Haus</label>
      <select
        id="hauswahl"
        aria-label="Haus wählen"
        value={haus == null ? '' : String(haus)}
        onChange={(e) => setHaus(e.target.value === '' ? null : Number(e.target.value))}
      >
        <option value="">Alle Häuser</option>
        {aktive.map((h) => (
          <option key={h.id} value={String(h.nummer)}>
            {hausText(h.nummer)}{h.name && h.name !== hausText(h.nummer) ? ` – ${h.name}` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}
